import { createListCollection, Select as ChakraSelect, mergeRefs } from '@chakra-ui/react';
import {
	SelectContent,
	SelectItem,
	SelectLabel,
	SelectRoot,
	SelectTrigger,
	SelectValueText,
	Field,
	FieldProps
} from '../chakra-components';
import { FieldValues, Path, PathValue, useController, useFormContext } from 'react-hook-form';
import React from 'react';
import { ModalHooks } from '../modal';
import { useWatchCallback } from './useWatchCallback';


interface SelectPropsBase<T extends FieldValues, TPath extends Path<T>> extends Omit<ChakraSelect.RootProps, 'collection'> {
	linkTo: TPath;
	placeholder: string;
	label?: React.ReactNode;
	disabled?: boolean;
	fieldProps?: FieldProps;
}

type ElementType<T> = T extends Array<infer U> ? U : T;

// Single select: options is an array of the field’s type.
export interface SingleSelectProps<T extends FieldValues, TPath extends Path<T>>
	extends SelectPropsBase<T, TPath> {
	multiple?: false;
	options: PathValue<T, TPath>[];
	onWatch?: (value: PathValue<T, TPath>) => void;
	renderOption: (option: PathValue<T, TPath>) => React.ReactNode;
	valueGetter: (option: PathValue<T, TPath>) => string;
}

export interface MultiSelectProps<T extends FieldValues, TPath extends Path<T>>
	extends SelectPropsBase<T, TPath> {
	multiple: true;
	onWatch?: (value: ElementType<PathValue<T, TPath>>[]) => void;
	options: ElementType<PathValue<T, TPath>>[]; // options are now element type
	renderOption: (option: ElementType<PathValue<T, TPath>>) => React.ReactNode;
	valueGetter: (option: ElementType<PathValue<T, TPath>>) => string;
}

export type SelectProps<T extends FieldValues, TPath extends Path<T>> =
	| SingleSelectProps<T, TPath>
	| MultiSelectProps<T, TPath>;

export type SelectRef = HTMLDivElement;

export const Select = <T extends FieldValues, TPath extends Path<T>>(props: SelectProps<T, TPath> & { ref?: React.Ref<SelectRef> }) => {

	const { ref, placeholder, fieldProps, linkTo, options, disabled, onWatch, renderOption, multiple, label, valueGetter, ...rootProps } = props;

	const collection = createListCollection<any>({
		items: options,
		itemToValue: valueGetter,
		itemToString: valueGetter,
	});

	const { contentRef } = ModalHooks.useModalContext();
	const { control } = useFormContext<T>();
	const { fieldState, field } = useController<T>({ name: linkTo, control, disabled });

	useWatchCallback(control, linkTo, onWatch);

	const isEmpty = Array.isArray(field.value) ? field.value.length === 0 : field.value === undefined || field.value === null || field.value === '';
	const invalid = fieldState.error?.message !== undefined;

	return (
		<Field errorText={fieldState.error?.message} invalid={invalid}  {...fieldProps} >
			<SelectRoot multiple={multiple}
				name={field.name}
				value={multiple ? field.value.map(valueGetter) : [valueGetter(field.value as any)]}
				onValueChange={({ items }) => field.onChange(multiple ? items : items[0])}
				onInteractOutside={() => field.onBlur()}
				size='sm'
				{...rootProps}
				ref={mergeRefs(ref, field.ref)}
				collection={collection}
			>
				{label && <SelectLabel>{label} {rootProps.required && <sup>*</sup>}</SelectLabel>}
				<SelectTrigger clearable={!isEmpty && !rootProps.required}>
					<SelectValueText placeholder={placeholder} color={isEmpty ? { base: '{colors.neutral.300}', _dark: '{colors.neutral.500}' } : undefined}/>
				</SelectTrigger>
				<SelectContent portalRef={contentRef}>
					{collection.items.map((option, index) => (
						<SelectItem item={option} key={index} >
							{renderOption(option)}
						</SelectItem>
					))}
				</SelectContent>
			</SelectRoot>
		</Field>
	);
};
